/**
 * Mobile/tablet: open the 3D model in a full-screen overlay via the gallery zoom button or model tap.
 */
(function () {
  const MOBILE_MEDIA_QUERY = window.matchMedia('screen and (max-width: 999px)');
  const TAP_MOVE_THRESHOLD = 12;

  /** @type {{ overlay: HTMLElement, modelMedia: Element, placeholder: HTMLElement, parent: Node, carousel: Element, onKeydown: (event: KeyboardEvent) => void, onCarouselChange: () => void } | null} */
  let activeOverlay = null;
  let lastOpenAt = 0;
  let savedScrollY = 0;

  function isMobileGallery() {
    return MOBILE_MEDIA_QUERY.matches;
  }

  function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  function isPosterButton(target) {
    return Boolean(
      target.closest('.shopify-model-viewer-ui__button--poster, button.button--poster')
    );
  }

  function getActiveModelMedia(carousel) {
    const cell = carousel.selectedCell;

    if (!cell || cell.getAttribute('data-media-type') !== 'model') {
      return null;
    }

    return cell.querySelector('model-media');
  }

  function ensureModelPlaying(modelMedia) {
    if (modelMedia.hasAttribute('playing')) {
      return;
    }

    const ui = modelMedia.querySelector('.shopify-model-viewer-ui');
    const poster =
      ui?.querySelector('.shopify-model-viewer-ui__button--poster') ??
      ui?.querySelector('button.button--poster');

    poster?.click();
  }

  function refreshModelViewer(modelMedia, content) {
    const modelViewer = modelMedia.querySelector('model-viewer');
    const ui = modelMedia.querySelector('.shopify-model-viewer-ui');

    if (!modelViewer) {
      return;
    }

    const rect = content.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width), 1);
    const height = Math.max(Math.round(rect.height), 1);

    for (const element of [modelMedia, ui, modelViewer]) {
      if (!element) {
        continue;
      }

      element.style.width = `${width}px`;
      element.style.height = `${height}px`;
    }

    if (typeof modelViewer.updateFraming === 'function') {
      modelViewer.updateFraming();
    }

    modelViewer.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new Event('resize'));
  }

  function createPlaceholder(modelMedia) {
    const placeholder = document.createElement('div');
    placeholder.className = 'model-media-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');

    const aspectRatio = modelMedia.style.aspectRatio || getComputedStyle(modelMedia).aspectRatio;
    if (aspectRatio && aspectRatio !== 'auto') {
      placeholder.style.aspectRatio = aspectRatio;
    } else {
      placeholder.style.minHeight = `${modelMedia.offsetHeight}px`;
    }

    return placeholder;
  }

  function createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'model-viewer-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <button type="button" class="model-viewer-overlay__close circle-button circle-button--sm" aria-label="Close">
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      </button>
      <div class="model-viewer-overlay__content"></div>
    `;
    return overlay;
  }

  function clearModelSizing(modelMedia) {
    const modelViewer = modelMedia.querySelector('model-viewer');
    const ui = modelMedia.querySelector('.shopify-model-viewer-ui');

    for (const element of [modelMedia, ui, modelViewer]) {
      if (!element) {
        continue;
      }

      element.style.removeProperty('width');
      element.style.removeProperty('height');
    }
  }

  function lockPageScroll() {
    savedScrollY = window.scrollY;
    document.body.style.top = `-${savedScrollY}px`;
    document.documentElement.classList.add('model-viewer-overlay-open');
  }

  function unlockPageScroll() {
    document.documentElement.classList.remove('model-viewer-overlay-open');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  function refreshCarousel(carousel) {
    carousel.style.removeProperty('max-height');

    const refresh = () => window.dispatchEvent(new Event('resize'));

    window.requestAnimationFrame(() => {
      refresh();
      window.requestAnimationFrame(refresh);
    });
  }

  function closeOverlay() {
    if (!activeOverlay) {
      return;
    }

    const { overlay, modelMedia, placeholder, parent, carousel, onKeydown, onCarouselChange } = activeOverlay;

    clearModelSizing(modelMedia);
    parent.insertBefore(modelMedia, placeholder);
    placeholder.remove();
    overlay.remove();
    unlockPageScroll();
    document.removeEventListener('keydown', onKeydown);
    carousel.removeEventListener('carousel:change', onCarouselChange);

    activeOverlay = null;
    refreshCarousel(carousel);
  }

  function openOverlay(modelMedia, carousel) {
    if (activeOverlay || !modelMedia?.parentNode) {
      return;
    }

    // Keep the iOS user-gesture chain alive before moving WebGL content.
    ensureModelPlaying(modelMedia);

    const parent = modelMedia.parentNode;
    const placeholder = createPlaceholder(modelMedia);
    parent.insertBefore(placeholder, modelMedia);

    const overlay = createOverlay();
    const content = overlay.querySelector('.model-viewer-overlay__content');
    content.appendChild(modelMedia);
    document.body.appendChild(overlay);
    lockPageScroll();

    const refresh = () => refreshModelViewer(modelMedia, content);

    window.requestAnimationFrame(() => {
      refresh();
      window.requestAnimationFrame(refresh);
    });

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        closeOverlay();
      }
    };

    const onCarouselChange = () => closeOverlay();

    const closeButton = overlay.querySelector('.model-viewer-overlay__close');
    closeButton?.addEventListener('click', closeOverlay);

    if (isTouchDevice()) {
      closeButton?.addEventListener(
        'touchend',
        (event) => {
          event.preventDefault();
          closeOverlay();
        },
        { passive: false }
      );
    }

    document.addEventListener('keydown', onKeydown);
    carousel.addEventListener('carousel:change', onCarouselChange);

    activeOverlay = {
      overlay,
      modelMedia,
      placeholder,
      parent,
      carousel,
      onKeydown,
      onCarouselChange,
    };
  }

  function requestOpenOverlay(modelMedia, carousel, event) {
    if (!isMobileGallery() || activeOverlay || !modelMedia) {
      return;
    }

    if (Date.now() - lastOpenAt < 500) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    event?.stopImmediatePropagation();

    lastOpenAt = Date.now();
    openOverlay(modelMedia, carousel);
  }

  function handleZoomOpen(event, gallery, carousel) {
    if (!gallery.classList.contains('product-gallery--model-active')) {
      return;
    }

    const modelMedia = getActiveModelMedia(carousel);
    requestOpenOverlay(modelMedia, carousel, event);
  }

  function syncZoomButton(gallery, carousel) {
    const zoomButton = gallery.querySelector('.product-gallery__zoom-button');
    if (!zoomButton || !carousel?.selectedCell) {
      return;
    }

    const isModel = carousel.selectedCell.getAttribute('data-media-type') === 'model';
    const shouldShowModelExpand = isMobileGallery() && isModel;

    gallery.classList.toggle('product-gallery--model-active', shouldShowModelExpand);

    if (shouldShowModelExpand) {
      zoomButton.classList.remove('product-gallery__zoom-button--hidden');
    }
  }

  function bindModelTap(gallery, carousel) {
    /** @type {{ x: number, y: number } | null} */
    let touchStart = null;

    carousel.addEventListener(
      'touchstart',
      (event) => {
        if (!isMobileGallery() || !gallery.classList.contains('product-gallery--model-active')) {
          return;
        }

        const modelMedia = getActiveModelMedia(carousel);
        if (!modelMedia || !modelMedia.contains(event.target)) {
          return;
        }

        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY };
      },
      { passive: true }
    );

    carousel.addEventListener(
      'touchend',
      (event) => {
        if (!touchStart) {
          return;
        }

        const start = touchStart;
        touchStart = null;

        if (!isMobileGallery() || !gallery.classList.contains('product-gallery--model-active')) {
          return;
        }

        const modelMedia = getActiveModelMedia(carousel);
        if (!modelMedia || !modelMedia.contains(event.target) || isPosterButton(event.target)) {
          return;
        }

        const touch = event.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;

        if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD) {
          return;
        }

        requestOpenOverlay(modelMedia, carousel, event);
      },
      { passive: false }
    );

    carousel.addEventListener(
      'click',
      (event) => {
        if (isTouchDevice() || !isMobileGallery() || !gallery.classList.contains('product-gallery--model-active')) {
          return;
        }

        const modelMedia = getActiveModelMedia(carousel);
        if (!modelMedia || !modelMedia.contains(event.target) || isPosterButton(event.target)) {
          return;
        }

        requestOpenOverlay(modelMedia, carousel, event);
      },
      true
    );
  }

  function bindZoomButton(gallery, carousel, zoomButton) {
    const onOpen = (event) => handleZoomOpen(event, gallery, carousel);

    zoomButton.addEventListener('click', onOpen, true);

    if (isTouchDevice()) {
      zoomButton.addEventListener(
        'touchend',
        (event) => {
          if (!gallery.classList.contains('product-gallery--model-active')) {
            return;
          }

          onOpen(event);
        },
        { capture: true, passive: false }
      );
    }
  }

  function initGallery(gallery) {
    const carousel = gallery.querySelector('.product-gallery__carousel');

    if (!carousel) {
      return;
    }

    const sync = () => syncZoomButton(gallery, carousel);

    carousel.addEventListener('carousel:settle', () => {
      window.requestAnimationFrame(sync);
      window.setTimeout(sync, 0);
    });

    sync();
    bindModelTap(gallery, carousel);

    const zoomButton = gallery.querySelector('.product-gallery__zoom-button');
    if (zoomButton) {
      bindZoomButton(gallery, carousel, zoomButton);
    }
  }

  function init() {
    document.querySelectorAll('product-gallery').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
